import { defineStore } from "pinia";
import { ref, computed } from "vue";
import client from "@/api/client";

export const useCycleStore = defineStore("cycle", () => {
  const currentCycle = ref(null);
  const currentCheckin = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const hasRevealedCheckin = computed(() => {
    const status = currentCheckin.value?.status;
    return status === "revealed" || status === "completed";
  });

  const hasAnyHistory = computed(
    () => hasRevealedCheckin.value || (currentCycle.value?.cycle_number || 0) > 1
  );

  const trailNavLocked = computed(() => {
    const checkin = currentCheckin.value;
    if (!checkin) return false;
    if (checkin.status === "revealed" || checkin.status === "completed") return false;
    return (checkin.tasks_count ?? 0) > 0 && (checkin.tasks_pending_count ?? 0) > 0;
  });

  const hasFetchedCurrent = ref(false);

  async function fetchCurrent() {
    loading.value = true;
    error.value = null;

    const [cycleResult, checkinResult] = await Promise.allSettled([
      client.get("/cycles/current"),
      client.get("/weekly_checkins/current"),
    ]);

    if (cycleResult.status === "fulfilled") {
      currentCycle.value = cycleResult.value.data.cycle;
    } else {
      currentCycle.value = null;
    }

    if (checkinResult.status === "fulfilled") {
      currentCheckin.value = checkinResult.value.data.checkin;
    } else {
      currentCheckin.value = null;
    }

    loading.value = false;
    hasFetchedCurrent.value = true;
    return { success: true };
  }

  async function ensureCurrent() {
    if (hasFetchedCurrent.value) return { success: true };
    return fetchCurrent();
  }

  async function fetchCheckin(id) {
    loading.value = true;
    try {
      const response = await client.get(`/weekly_checkins/${id}`);
      return { success: true, checkin: response.data.checkin };
    } catch (e) {
      return { success: false, error: e.response?.data?.error };
    } finally {
      loading.value = false;
    }
  }

  function reset() {
    currentCycle.value = null;
    currentCheckin.value = null;
    error.value = null;
    hasFetchedCurrent.value = false;
  }

  return {
    currentCycle,
    currentCheckin,
    loading,
    error,
    hasRevealedCheckin,
    hasAnyHistory,
    trailNavLocked,
    hasFetchedCurrent,
    fetchCurrent,
    ensureCurrent,
    fetchCheckin,
    reset,
  };
});
